import { generateId, TIPTAP_IMAGE_TYPE } from 'ricos-content';
import imageDataDefaults from 'ricos-schema/dist/statics/image.defaults.json';
import {
  type ExtensionProps,
  type NodeConfig,
  type RicosExtension,
  type RicosServices,
} from 'ricos-types';
import { createRicosNodeExtension, Uploader } from 'wix-rich-content-plugin-commons';

import { ImagePluginService } from '../toolbar/imagePluginService';
import { EditorImage as Component } from './component';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      /**
       * Toggle a paragraph
       */
      setImageUrl: (url: string) => ReturnType;
      /**
       * Update Image's loading state
       */
      setImageLoading: (isLoading: boolean) => ReturnType;
    };
  }
}

const name = TIPTAP_IMAGE_TYPE;

export const tiptapExtensions = [
  createRicosNodeExtension({
    type: 'react-node',
    name,
    groups: ['spoilerable', 'resizable', 'draggable', 'not-overtypable'],
    reconfigure: (
      config: NodeConfig,
      _extensions: RicosExtension[],
      _props: ExtensionProps,
      settings: Record<string, unknown>,
      services: RicosServices
    ) => ({
      ...config,
      addOptions: () => ({
        services,
        imagePluginService: new ImagePluginService(),
        ...settings,
      }),
    }),
    Component,
    createExtensionConfig({ Plugin, PluginKey }) {
      return {
        name: this.name,
        atom: false,
        group: 'block',
        selectable: true,
        draggable: true,
        addAttributes: () => ({
          ...imageDataDefaults,
          loading: {
            default: false,
          },
          loadingPercentage: {
            default: null,
          },
          tempData: {
            default: null,
          },
        }),
        addProseMirrorPlugins() {
          return [
            new Plugin({
              key: new PluginKey('handlePasteImage'),
              props: {
                handlePaste: (_, event) => {
                  let hasFiles = false;

                  // Prefer text to images.
                  // This check solves issue with MacOS + MS Word, where copied text
                  // will also have `image/png` inside clipboardData, resulting in pasting
                  // image instead of text
                  if (event?.clipboardData?.types.includes('text/plain')) {
                    return false;
                  }

                  Array.from(event?.clipboardData?.files || [])
                    .filter((file: File) => file.type.startsWith('image'))
                    .forEach(file => {
                      const nodeId = generateId();
                      this.editor.commands.insertContent({
                        type: TIPTAP_IMAGE_TYPE,
                        attrs: { id: nodeId, ...imageDataDefaults },
                      });
                      this.options.services?.uploadService?.uploadFile(
                        file,
                        nodeId,
                        new Uploader(this.options.handleFileUpload),
                        TIPTAP_IMAGE_TYPE,
                        this.options.imagePluginService
                      );
                      hasFiles = true;
                    });
                  if (hasFiles) {
                    event.preventDefault();
                    return true;
                  }
                  return false;
                },
              },
            }),
          ];
        },

        addCommands() {
          return {
            setImageUrl:
              url =>
              ({ commands }) => {
                return commands.updateAttributes(name, { image: { src: { custom: url } } });
              },
            setImageLoading:
              loading =>
              ({ commands }) => {
                return commands.updateAttributes(name, { loading });
              },
          };
        },
      };
    },
  }),
];
